import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Edit,
  FileSpreadsheet,
  MapPin,
  Plus,
  Printer,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import { printHtmlInSameTab } from '../Reports/reportPrintHelpers';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';

const PAGE_SIZE = 20;

const emptyCharge = {
  Id: 0,
  DeliveryCharges: '',
  AreaId: '',
  ClientId: '',
  Client: { Name: '', MobileNo1: '' },
  Area: {},
  IsActive: true,
};

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseAreas = (response) => {
  if (Array.isArray(response?.Areas)) return response.Areas;
  if (Array.isArray(response?.areas)) return response.areas;
  return [];
};

const getStatus = (response) => (typeof response === 'string' ? response.trim() : String(response?.Status ?? response?.status ?? '').trim());
const getMessage = (response, fallback) => {
  if (typeof response === 'string' && response.trim()) return response.trim();
  return response?.Message || response?.message || response?.Status || response?.status || fallback;
};

const buildClientRequest = (keyword, field = 'Name') => ({
  filter: {
    filters: [{ field, operator: 'contains', value: keyword }],
    logic: 'and',
  },
});

const ClientAreaCharges = () => {
  const [rows, setRows] = useState([]);
  const [areas, setAreas] = useState([]);
  const [formData, setFormData] = useState(emptyCharge);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientPicker, setClientPicker] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const clientSearchSeq = useRef(0);

  const pageSize = viewAll ? 100000 : PAGE_SIZE;
  const totalPages = viewAll ? 1 : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isEditing = Boolean(formData.Id);
  const selectedArea = useMemo(() => areas.find((area) => Number(area.Id) === Number(formData.AreaId)), [areas, formData.AreaId]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.ClientAreaChargesPriceGrid({
        Keyword: activeSearchKeyword,
        Skip: viewAll ? 0 : (currentPage - 1) * PAGE_SIZE,
        Take: pageSize,
      });

      setRows(parseList(response));
      setAreas(parseAreas(response));
      setTotalCount(Number(response?.Total) || Number(response?.total) || 0);
    } catch (err) {
      console.error('Load client area charge grid error:', err);
      setError('Failed to load client area charges');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeSearchKeyword, currentPage, pageSize, viewAll]);

  const loadClients = useCallback(async (keyword, field = 'Name') => {
    const trimmed = keyword.trim();
    if (trimmed.length < 2) {
      setClientOptions([]);
      return;
    }

    const seq = clientSearchSeq.current + 1;
    clientSearchSeq.current = seq;

    try {
      setClientLoading(true);
      const response = await MainService.ClientsInAutoComplete(buildClientRequest(trimmed, field));
      if (seq !== clientSearchSeq.current) return;
      setClientOptions(parseList(response));
    } catch (err) {
      console.error('Load clients error:', err);
      if (seq === clientSearchSeq.current) setClientOptions([]);
    } finally {
      if (seq === clientSearchSeq.current) setClientLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!clientPicker) return undefined;
    const timer = setTimeout(() => loadClients(clientPicker.keyword, clientPicker.field), 250);
    return () => clearTimeout(timer);
  }, [clientPicker, loadClients]);

  const resetForm = () => {
    setFormData(emptyCharge);
    setClientOptions([]);
    setClientPicker(null);
  };

  const openNewForm = () => {
    resetForm();
    setFormOpen(true);
  };

  const openCellForm = (row, cell) => {
    setSelectedClientId(row.ClientId);
    const area = areas.find((item) => Number(item.Id) === Number(cell.AreaId)) || { Id: cell.AreaId, Name: cell.AreaName };
    setFormData({
      ...emptyCharge,
      Id: Number(cell.ChargeId || cell.chargeId || 0),
      DeliveryCharges: Number(cell.DeliveryCharges || cell.deliveryCharges || 0) || '',
      AreaId: cell.AreaId,
      ClientId: row.ClientId,
      Client: row.Client || { Id: row.ClientId, Name: '', MobileNo1: '' },
      Area: area,
      IsActive: true,
    });
    setFormOpen(true);
  };

  const setClientValue = (patch) => {
    setFormData((prev) => ({
      ...prev,
      ...patch,
      Client: { ...(prev.Client || {}), ...(patch.Client || {}) },
    }));
  };

  const selectClient = (client) => {
    setClientValue({
      ClientId: client.Id,
      Client: {
        Id: client.Id,
        Name: client.Name || '',
        MobileNo1: client.MobileNo1 || '',
      },
    });
    setClientOptions([]);
    setClientPicker(null);
  };

  const clientListOpen = Boolean(clientPicker) && !clientLoading && clientOptions.length > 0;
  const clientNav = useListKeyboardNav({
    isOpen: clientListOpen,
    itemCount: clientOptions.length,
    onSelectIndex: (index) => clientOptions[index] && selectClient(clientOptions[index]),
    onClose: () => setClientPicker(null),
    resetKey: `${clientPicker?.field || ''}:${clientPicker?.keyword || ''}`,
  });

  const saveCharge = async () => {
    if (!formData.ClientId) {
      setError('Select Client');
      return;
    }

    if (!formData.AreaId) {
      setError('Select Area');
      return;
    }

    const charge = Number(formData.DeliveryCharges);
    if (!Number.isFinite(charge) || charge <= 0) {
      setError('Enter Delivery Charge');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveClientAreaCharges({
        ...formData,
        AreaId: Number(formData.AreaId),
        ClientId: Number(formData.ClientId),
        DeliveryCharges: charge,
        Area: selectedArea || formData.Area,
        IsActive: formData.IsActive !== false,
      });

      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess(isEditing ? 'Client area charge updated successfully' : 'Client area charge saved successfully');
        setFormOpen(false);
        resetForm();
        await loadRows();
      } else {
        setError(getMessage(response, 'Failed to save client area charge'));
      }
    } catch (err) {
      console.error('Save client area charge error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCharge = async () => {
    const chargeId = Number(deleteTarget?.Id || 0);
    if (!chargeId) return;

    try {
      setLoading(true);
      const response = await MainService.DeleteClientAreaCharge(chargeId);
      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess('Deleted');
        setDeleteTarget(null);
        await loadRows();
      } else {
        setError(getMessage(response, 'Failed to delete client area charge'));
      }
    } catch (err) {
      console.error('Delete client area charge error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchTerm.trim());
  };

  const clearSearch = () => {
    setSearchTerm('');
    setActiveSearchKeyword('');
    setCurrentPage(1);
  };

  const cellValue = (cell) => Number(cell?.DeliveryCharges ?? cell?.deliveryCharges ?? 0);
  const cellChargeId = (cell) => Number(cell?.ChargeId ?? cell?.chargeId ?? 0);

  const buildExportRows = (gridRows, gridAreas) => gridRows.map((row) => {
    const item = {
      Client: row.Client?.Name || '',
      Mobile: row.Client?.MobileNo1 || '',
    };

    gridAreas.forEach((area) => {
      const cell = (row.Areas || row.areas || []).find((areaCell) => Number(areaCell.AreaId ?? areaCell.areaId) === Number(area.Id));
      item[area.Name || `Area ${area.Id}`] = cellValue(cell).toFixed(2);
    });

    return item;
  });

  const exportExcel = async () => {
    try {
      setLoading(true);
      const response = await MainService.ClientAreaChargesPriceGrid({
        Keyword: activeSearchKeyword,
        Skip: 0,
        Take: 100000,
      });
      const exportAreas = parseAreas(response);
      const exportRows = parseList(response);
      const worksheetData = buildExportRows(exportRows.length ? exportRows : rows, exportAreas.length ? exportAreas : areas);

      if (!worksheetData.length) {
        setError('No data to export');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Client Area Charges');
      XLSX.writeFile(workbook, `Client_Area_Charges_${new Date().toISOString().split('T')[0]}.xlsx`);
      setSuccess('Excel exported successfully');
    } catch (err) {
      console.error('Export client area charge grid error:', err);
      setError('Failed to export Excel');
    } finally {
      setLoading(false);
    }
  };

  const printClientPriceCard = async (row) => {
    setSelectedClientId(row.ClientId);
    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const clientName = row.Client?.Name || '-';
    const mobileNo = row.Client?.MobileNo1 || '-';
    const priceRows = areas.map((area) => {
      const cell = (row.Areas || row.areas || []).find((item) => Number(item.AreaId ?? item.areaId) === Number(area.Id));
      return {
        areaName: area.Name || '-',
        price: cellValue(cell).toFixed(2),
      };
    });

    let printHeaderHtml = '';
    try {
      printHeaderHtml = (await getPrintHeaderHtml()) || '';
    } catch (err) {
      console.warn('Client area price card print: no print header', err);
    }

    const headerBlock = printHeaderHtml
      ? `<div class="company-print-header">${printHeaderHtml}</div>`
      : '';

    const htmlDoc = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>&#x200b;</title>
          <style>
            html, body { margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; color: #111827; font-size: 12px; }
            .print-inner { box-sizing: border-box; padding: 14mm 16mm 16mm 16mm; }
            .company-print-header { margin-bottom: 14px; }
            .company-print-header img { max-height: 72px; }
            .header { border-bottom: 2px solid #16a34a; padding-bottom: 14px; margin-bottom: 18px; }
            h1 { margin: 0; font-size: 24px; }
            .meta { margin-top: 8px; color: #4b5563; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; }
            th, td { border: 1px solid #d1d5db; padding: 10px 12px; text-align: left; }
            th { background: #f3f4f6 !important; font-size: 12px; text-transform: uppercase; color: #4b5563; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            td.price { text-align: right; font-weight: 700; }
            @media print {
              @page { size: A4 portrait; margin: 14mm 16mm 16mm 16mm; }
              html, body { margin: 0 !important; padding: 0 !important; }
              .print-inner { padding: 0 !important; }
              .company-print-header { page-break-inside: avoid; }
              thead { display: table-header-group; }
              tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-inner">
            ${headerBlock}
            <div class="header">
              <h1>Client Area Price Card</h1>
              <div class="meta"><strong>Client:</strong> ${escapeHtml(clientName)}</div>
              <div class="meta"><strong>Mobile:</strong> ${escapeHtml(mobileNo)}</div>
              <div class="meta"><strong>Date:</strong> ${escapeHtml(new Date().toLocaleDateString())}</div>
            </div>
            <table>
              <thead>
                <tr><th>Area</th><th style="text-align:right;">Delivery Charge</th></tr>
              </thead>
              <tbody>
                ${priceRows.map((item) => `<tr><td>${escapeHtml(item.areaName)}</td><td class="price">${escapeHtml(item.price)}</td></tr>`).join('')}
              </tbody>
            </table>
          </div>
        </body>
      </html>
    `;

    try {
      await printHtmlInSameTab(htmlDoc, 'Client area price card print');
    } catch (err) {
      console.error('Client area price card print error:', err);
      setError('Failed to print price card');
    }
  };

  const clientDropdown = (field) => {
    if (!clientPicker || clientPicker.field !== field) return null;

    return (
      <div className="absolute z-40 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl" role="listbox">
        {clientLoading ? (
          <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading clients...
          </div>
        ) : clientOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500">Type at least 2 letters or numbers to search clients.</div>
        ) : (
          clientOptions.map((client, index) => (
            <button
              key={client.Id}
              type="button"
              role="option"
              aria-selected={index === clientNav.highlightedIndex}
              ref={(node) => clientNav.setItemRef(index, node)}
              onMouseEnter={() => clientNav.setHighlightedIndex(index)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectClient(client)}
              className={`block w-full px-4 py-3 text-left ${
                index === clientNav.highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <div className="text-sm font-semibold text-gray-900">{client.Name || '-'}</div>
              <div className="text-xs text-gray-500">{client.MobileNo1 || '-'}</div>
            </button>
          ))
        )}
      </div>
    );
  };

  const clientInput = ({ field, label, placeholder, value }) => (
    <div className="relative">
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      <input
        value={value || ''}
        onChange={(e) => {
          const nextValue = e.target.value;
          setClientValue({
            ClientId: '',
            Client: { [field === 'MobileNo1' ? 'MobileNo1' : 'Name']: nextValue },
          });
          setClientPicker({ field, keyword: nextValue });
        }}
        onFocus={() => setClientPicker({ field, keyword: value || '' })}
        onBlur={() => setTimeout(() => setClientPicker(null), 150)}
        onKeyDown={clientNav.onKeyDown}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {clientDropdown(field)}
    </div>
  );

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <MapPin className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Client Area Charges</h1>
              <p className="mt-1 text-sm text-gray-500">Price grid grouped by client. Each area is a column, and empty charges show as 0.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openNewForm}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
            >
              <Plus className="h-4 w-4" />
              Add Charge
            </button>
            <button
              type="button"
              onClick={loadRows}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <MapPin className="h-5 w-5 text-primary" />
                Price Grid
              </h2>
              <p className="mt-1 text-sm text-gray-500">Click any price cell to add or edit that client-area charge.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applySearch();
                  }}
                  className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Search client / mobile / area"
                />
              </div>
              <button type="button" onClick={applySearch} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700">
                Search
              </button>
              <button type="button" onClick={clearSearch} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200">
                Clear
              </button>
              <button
                type="button"
                onClick={exportExcel}
                disabled={loading || rows.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excel
              </button>
            </div>
          </div>
        </div>

        <div className="max-h-[660px] overflow-auto">
          <table className="min-w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-20 bg-gray-50 shadow-sm">
              <tr>
                <th className="sticky left-0 z-30 min-w-[260px] border-b border-r border-gray-200 bg-gray-50 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Client
                </th>
                {areas.map((area) => (
                  <th
                    key={area.Id}
                    className="min-w-[130px] border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                    title={area.Name}
                  >
                    {area.Name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {loading ? (
                <tr>
                  <td colSpan={Math.max(1, areas.length + 1)} className="px-5 py-16 text-center text-gray-500">
                    <RefreshCw className="mx-auto mb-2 h-8 w-8 animate-spin text-primary" />
                    Loading price grid...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(1, areas.length + 1)} className="px-5 py-16 text-center text-gray-500">
                    No client area charges found
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const isSelected = Number(selectedClientId) === Number(row.ClientId);

                  return (
                  <tr
                    key={row.ClientId}
                    onClick={() => setSelectedClientId(row.ClientId)}
                    className={`group cursor-pointer transition-colors ${isSelected ? 'bg-amber-50' : ''}`}
                  >
                    <td className={`sticky left-0 z-10 border-b border-r px-5 py-4 transition-colors ${
                      isSelected
                        ? 'border-amber-200 bg-amber-100'
                        : 'border-gray-100 bg-white group-hover:bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-semibold text-gray-900">{row.Client?.Name || '-'}</div>
                          <div className="text-xs text-gray-500">{row.Client?.MobileNo1 || '-'}</div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            printClientPriceCard(row);
                          }}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                          title="Print price card"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    {areas.map((area) => {
                      const cell = (row.Areas || row.areas || []).find((item) => Number(item.AreaId ?? item.areaId) === Number(area.Id)) || {
                        AreaId: area.Id,
                        AreaName: area.Name,
                        ChargeId: 0,
                        DeliveryCharges: 0,
                      };
                      const value = cellValue(cell);
                      const chargeId = cellChargeId(cell);

                      return (
                        <td
                          key={`${row.ClientId}-${area.Id}`}
                          className={`border-b border-r p-2 text-center transition-colors ${
                            isSelected
                              ? 'border-amber-200 bg-amber-50'
                              : 'border-gray-100 group-hover:bg-gray-50'
                          }`}
                        >
                          <div className={`rounded-lg px-2 py-2 ring-1 ${
                            value > 0
                              ? isSelected ? 'bg-green-100 text-green-900 ring-green-200' : 'bg-green-50 text-green-800 ring-transparent'
                              : isSelected ? 'bg-white text-gray-700 ring-amber-200' : 'bg-gray-50 text-gray-500 ring-transparent'
                          }`}>
                            <button
                              type="button"
                              onClick={() => openCellForm(row, cell)}
                              className="block w-full text-sm font-semibold"
                              title={value > 0 ? 'Edit charge' : 'Add charge'}
                            >
                              {value > 0 ? value.toFixed(2) : '0'}
                            </button>
                            {chargeId > 0 && (
                              <div className="mt-1 flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => openCellForm(row, cell)}
                                  className="rounded p-1 text-yellow-600 hover:bg-yellow-100"
                                  title="Edit"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedClientId(row.ClientId);
                                    setDeleteTarget({
                                    Id: chargeId,
                                    Client: row.Client,
                                    Area: area,
                                    });
                                  }}
                                  className="rounded p-1 text-red-600 hover:bg-red-100"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-gray-600">
              Showing {rows.length ? (viewAll ? 1 : (currentPage - 1) * PAGE_SIZE + 1) : 0} to {viewAll ? rows.length : Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} clients
            </p>
            <button
              type="button"
              onClick={() => {
                setCurrentPage(1);
                setViewAll((prev) => !prev);
              }}
              disabled={loading}
              className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-primary ring-1 ring-primary/30 hover:bg-primary/5 disabled:opacity-50"
            >
              {viewAll ? 'Show Pages' : 'View All'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={viewAll || currentPage <= 1 || loading}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={viewAll || currentPage >= totalPages || loading}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {formOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{isEditing ? 'Update Client Area Charge' : 'Add Client Area Charge'}</h3>
                <p className="mt-1 text-sm text-gray-500">Choose client, area, and delivery charge.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormOpen(false);
                  setClientPicker(null);
                }}
                disabled={saving}
                className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              {clientInput({
                field: 'Name',
                label: 'Client',
                placeholder: 'Search by client name',
                value: formData.Client?.Name,
              })}
              {clientInput({
                field: 'MobileNo1',
                label: 'Client Mobile No',
                placeholder: 'Search by mobile no',
                value: formData.Client?.MobileNo1,
              })}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Area</label>
                <select
                  value={formData.AreaId || ''}
                  onChange={(e) => {
                    const areaId = Number(e.target.value) || '';
                    const area = areas.find((item) => Number(item.Id) === Number(areaId)) || {};
                    setFormData((prev) => ({ ...prev, AreaId: areaId, Area: area }));
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Area</option>
                  {areas.map((area) => (
                    <option key={area.Id} value={area.Id}>{area.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Delivery Charge</label>
                <input
                  type="number"
                  value={formData.DeliveryCharges}
                  onChange={(e) => setFormData((prev) => ({ ...prev, DeliveryCharges: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Clear
              </button>
              <button
                type="button"
                onClick={saveCharge}
                disabled={saving}
                className="inline-flex min-w-32 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEditing ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {deleteTarget && ReactDOM.createPortal(
        <ConfirmDialog
          loading={loading}
          message={`Delete charge for "${deleteTarget.Client?.Name || '-'}" / "${deleteTarget.Area?.Name || '-'}"?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={deleteCharge}
        />,
        document.body
      )}
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-20 z-50 flex min-w-[320px] items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
      {isError ? <AlertTriangle className="h-5 w-5 flex-shrink-0" /> : <CheckCircle className="h-5 w-5 flex-shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} className={isError ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const ConfirmDialog = ({ loading, message, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
    <div className="mx-4 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
        <div className="rounded-full bg-red-100 p-2 text-red-600">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
          <p className="mt-1 text-sm text-gray-500">{message}</p>
        </div>
        <button type="button" onClick={onCancel} disabled={loading} className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
        <button type="button" onClick={onCancel} disabled={loading} className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Confirm
        </button>
      </div>
    </div>
  </div>
);

export default ClientAreaCharges;
